/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Chaitanya
*/

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Toast from 'react-native-toast-message';
import { View, StyleSheet, ScrollView, Dimensions, Text } from 'react-native';

import SociollaText from '@components/sociolla/SociollaText';
import { translate } from '@global/translate';
import Button from '@components/form/Button';
import Input from '@components/form/Input';
import DatePicker from '@components/form/DatePicker';
import SelectBox from '@components/form/SelectBox';
import * as Layout from '@components/screen/Layout';
import FileInput from '@components/form/FileInput';
import AttachmentsInput from '@components/form/AttachmentsInput';
import RenderIf from '@components/global/RenderIf';
import SkpInternalBrandBudgetTable from './components/SkpExternalBudgetTable';
import SkpExternalBudgetTableModal from './components/SkpExternalBudgetTableModal';
import RadioButton from '@components/form/RadioButton';

import { isWeb, isMobileWeb, isAndroid } from '@global/screens';
import Colors from '@global/colors';
import { showPopup } from '@/store/modal';
import useApi from '@/hooks/useApi';
import { MsOrder } from '@api/ms-order';
import { MsB2b } from '@api/ms-b2b';
import { MsAccount } from '@api/ms-account';
import {
  formatCoaTaxLabel,
  getSkpInternalTaxes,
  currency,
  isSkpType,
  removeDuplicate,
  getFileNameFromUrl,
} from '@global/helpers';
import { showAlert } from '@/store/global';

const dimension = Dimensions.get('screen');

const CLAIM_TYPE_OPTIONS = [
  { label: 'Full Claim', value: 'full' },
  { label: 'Partial Claim', value: 'partial' },
];

const SkpExternalFormScreen = (props) => {
  const dispatch = useDispatch();
  const formRef = useRef();

  const FormValidationSchema = Yup.object().shape({
    skp_internal: Yup.object().required('skp reference is required.'),
    skp_name: Yup.string().required('skp name is required.'),
    from_date: Yup.string().required('from date is required.'),
    to_date: Yup.string().required('to date is required.'),
    coa_activity_promo: Yup.string(),
    is_active: Yup.boolean(),
    customer: Yup.object().required('customer is required.'),
    customer_name: Yup.string().required('customer code is required.'),
    sales_team: Yup.object().required('sales team is required.'),
    skp_external_account_no: Yup.string().when([], {
      is: () => !isGt,
      then: Yup.string().required('skp external account number is required.'),
      otherwise: Yup.string(),
    }),
    kwitansi_no: Yup.string().when([], {
      is: () => !isGt,
      then: Yup.string().required('Invoice/kwitansi number is required.'),
      otherwise: Yup.string(),
    }),
    grossup_pph: Yup.boolean().required('grossup pph is required.'),
    total_biaya: Yup.number().required('Total Biaya/DPP is required.'),
    pph: Yup.number().required('PPH is required.'),
    ppn: Yup.number().required('PPN is required.'),
    vat: Yup.object().required('VAT is required.'),
    total_nett: Yup.number().required('total nett is required.'),
    reference_invoice_potong: Yup.string().required(
      'reference invoice potong is required.',
    ),
    notes: Yup.string(),
    tax: Yup.object().required('Tax (%) is required.'),
    claim_type: Yup.string().when([], {
      is: () => !isGt,
      then: Yup.string().required('Claim Type is required.'),
      otherwise: Yup.string(),
    }),
  });

  const [detailData, setDetailData] = useState({});
  const [isEdit, setIsEdit] = useState(false);

  // SKP Internal State
  const [skpInternalData, setSkpInternalData] = useState();
  const [skpInternalsBrandBugets, setSkpInternalsBrandBugets] = useState([]);
  const [skpInternals, setSkpInternals] = useState([]);
  const [skpInternalsLoading, setSkpInternalsLoading] = useState(false);
  const [skpInternalsTableLoading, setSkpInternalsTableLoading] =
    useState(false);
  const [skpInternalParams, setSkpInternalParams] = useState({
    skip: 0,
    limit: 10,
    loading: false,
  });

  // Customer State
  const [skpInternalCustomersLoading, setSkpInternalCustomersLoading] =
    useState(false);
  const [skpInternalCustomerParams, setSkpInternalCustomerParams] = useState({
    skip: 0,
    limit: 10,
    loading: false,
  });
  const [skpInternalCustomers, setSkpInternalCustomers] = useState([]);

  // Outlet State
  const [skpInternalOutletsLoading, setSkpInternalOutletsLoading] =
    useState(false);
  const [skpInternalOutletParams, setSkpInternalOutletParams] = useState({
    skip: 0,
    limit: 10,
    loading: false,
  });
  const [skpInternalOutlets, setSkpInternalOutlets] = useState([]);

  const [isReady, setIsReady] = useState(false);
  const [isTableChanged, setIsTableChanged] = useState(false);
  const [taxesData, setTaxesData] = useState();
  const [coaTaxData, setCoaTaxData] = useState();
  const [taxType, setTaxType] = useState('');
  const [isLoadMoreCustomer, setIsLoadMoreCustomer] = useState(true);
  const editActualClaim = useSelector((state) => state.modal.editActualClaim);

  const getSkpExternalDetail = useApi(MsOrder.getSkpExternalDetail);
  const getSkpInternal = useApi(MsOrder.getSkpInternal);
  const searchSkpInternal = useApi(MsB2b.getSkpInternal);
  const getAllSkpInternalsBrandBudget = useApi(
    MsOrder.getAllSkpInternalsBrandBudget,
  );
  const getAllSkpExternalsBrandBudget = useApi(
    MsOrder.getAllSkpExternalsBrandBudget,
  );
  const getCoaTaxSettingsDetail = useApi(MsOrder.getCoaTaxSettingsDetail);
  const getSkpInternalCustomers = useApi(MsOrder.getSkpInternalCustomers);
  const getSkpInternalOutlets = useApi(MsOrder.getSkpInternalCustomers);
  const getCustomerSalesTeam = useApi(MsAccount.getCustomerSalesTeam);

  const getDetail = async (id) => {
    const response = await getSkpExternalDetail.request(id);
    if (response?.data?.data && Object.keys(response.data.data).length) {
      const result = response.data.data;
      result.id = result._id;
      if (result.customer?.code) {
        result.customer.customer_code = result.customer.code;
      }
      if (result.customer?.id) {
        const isSubdisType =
          result.skp_internal.skp_type?.toLowerCase() == 'subdistributor';
        const outlet_id = isSubdisType ? result.outlet?.id : null;
        getSkpExternalsBrandBudgets(result.id, result.customer.id, outlet_id);

        if (isSubdisType) {
          if (!result.outlet) {
            Toast.show({
              text1: 'Outlet is missing. Please update the SKP Internal first.',
              type: 'error',
            });
          } else {
            setCustomerTaxType(result.outlet);
          }
        } else {
          setCustomerTaxType(result.customer);
        }
      }

      let coaData = null;
      if (result.skp_internal?.coa_tax?.id) {
        coaData = await getCoaTax(result.skp_internal.coa_tax.id);
      }
      if (result.customer?.id && coaData) {
        const taxData = getTaxPercentage(
          result,
          coaData,
          result.skp_internal,
          result.customer,
          result.outlet,
        );

        // Handle old skp data
        if (!result.tax) {
          result.tax = taxData;
        }
      }

      if (!result.claim_type) {
        result.claim_type = 'full';
      }

      if (result?.documents?.length) {
        result.documents.map((doc) => {
          if (doc.type == 'kwitansi') {
            result.kwitansi = doc.url;
          } else if (doc.type == 'faktur_pajak') {
            result.faktur_pajak = doc.url;
          } else if (doc.type == 'foto_bukti_tayang') {
            result.foto_bukti_tayang = doc.url;
          } else if (doc.type == 'signed_skp') {
            result.signed_skp = doc.url;
          } else if (doc.type == 'foto_buku_rekening') {
            result.foto_buku_rekening = doc.url;
          } else if (doc.type == 'payment_proof') {
            result.payment_proof = doc.url;
          } else if (doc.type == 'other') {
            result.other = [
              ...(result.other || []),
              { name: getFileNameFromUrl(doc.url, 'Other File'), url: doc.url },
            ];
          }
        });
      }

      const marketType =
        result.skp_internal.market_type?.code?.toLowerCase() || '';
      result.skp_internal.skp_type = result.skp_internal.skp_type || marketType;

      setDetailData(result);

      setIsReady(true);
    }
  };

  const setCustomerTaxType = (cus) => {
    const taxTypeMap = {
      npwp_badan: 'tax_corporate',
      npwp_pribadi: 'tax_personal',
    };
    setTaxType(taxTypeMap[cus.wht] || '');
  };

  const getSalesTeambyCustomer = async (customerID) => {
    if (customerID) {
      const response = await getCustomerSalesTeam.request(customerID, {
        params: {
          fields: 'sales_team',
        },
      });

      if (response.status == 200 && response.data.data) {
        const sales_team = response.data.data.sales_team;
        formRef.current.setFieldValue('sales_team', sales_team);
      }
    }
  };

  const getSkpInternals = async ({ val = null, skip = 0 }) => {
    if (skip == 0) {
      setSkpInternalsLoading(true);
    }
    setSkpInternalParams({
      ...skpInternalParams,
      loading: skip > 0,
      skip: skip,
    });
    const payload = {
      params: {
        filter: {
          status: { $in: ['approved', 'archived'] },
          is_active: true,
        },
        skip: skip,
        limit: skpInternalParams.limit,
        sort: '-_id',
      },
    };

    if (val) {
      payload.params.filter.keyword = val;
    }
    let response;
    if (val) {
      response = await searchSkpInternal.request(payload);
    } else {
      response = await getSkpInternal.request(payload);
    }

    if (response.status == 200 && response?.data?.data?.length) {
      const old_result = JSON.parse(JSON.stringify(skpInternals));
      const result = response.data.data.map((item) => {
        if (item._id) {
          item.id = item._id;
        }
        return item;
      });
      let finalResult = result;
      if (skip > 0) {
        finalResult = old_result.concat(result);
      }
      setSkpInternals(finalResult);
      if (skip > 0) {
        setSkpInternalParams({
          ...skpInternalParams,
          loading: false,
          skip: skip,
        });
      }
    } else {
      if (skip <= 0) {
        setSkpInternals([]);
      }
      setSkpInternalParams({
        ...skpInternalParams,
        loading: false,
        skip: skip,
      });
    }
    setSkpInternalsLoading(false);
  };

  const findSkpInternalCustomers = async ({
    val = null,
    skip = 0,
    skp_internal_id,
  }) => {
    if (skip == 0) {
      setSkpInternalCustomersLoading(true);
    }
    setSkpInternalCustomerParams({
      ...skpInternalCustomerParams,
      loading: skip > 0,
      skip: skip,
    });
    const payload = {
      params: {
        filter: {
          check_claim: true,
        },
        skip: skip,
        limit: skpInternalParams.limit,
        sort: '-_id',
      },
    };

    if (val) {
      payload.params.filter['$or'] = [
        { 'customer.code': { $regex: val, $options: 'i' } },
        { 'customer.name': { $regex: val, $options: 'i' } },
      ];
    }
    const response = await getSkpInternalCustomers.request(
      skp_internal_id,
      payload,
    );

    if (response.status == 200 && response?.data?.data?.length) {
      const old_result = JSON.parse(JSON.stringify(skpInternalCustomers));
      const skpType = formRef.current.values.skp_internal.skp_type;
      const result = response.data.data.map((item) => {
        if (item._id) {
          const isFullClaim = item.fully_claimed ? true : false;
          item.id = item._id;
          item.label = `${item.customer.code} - ${item.customer.name} ${
            isFullClaim && skpType != 'subdistributor' ? '(Fully Claimed)' : ''
          }`.trim();
          item.code = item.customer.code;
          if (skpType != 'subdistributor') {
            if (detailData?.customer?.id != item.customer.id) {
              item.disabled = isFullClaim;
            }
          }
        }
        return item;
      });
      if (result?.length < skpInternalCustomerParams.limit) {
        setIsLoadMoreCustomer(false);
      } else {
        setIsLoadMoreCustomer(true);
      }
      let finalResult = result.filter(
        (obj, index, self) =>
          index === self.findIndex((o) => o.code === obj.code),
      );
      if (skip > 0) {
        finalResult = old_result.concat(result);
        finalResult = removeDuplicate(finalResult, 'customer.id');
      }
      setSkpInternalCustomers(finalResult);
      if (skip > 0) {
        setSkpInternalCustomerParams({
          ...skpInternalCustomerParams,
          loading: false,
          skip: skip,
        });
      }
    } else {
      if (skip <= 0) {
        setSkpInternalCustomers([]);
      }
      setSkpInternalCustomerParams({
        ...skpInternalCustomerParams,
        loading: false,
        skip: skip,
      });
    }
    setSkpInternalCustomersLoading(false);
  };

  const findSkpInternalOutlets = async ({
    val = null,
    skip = 0,
    skp_internal_id,
    customer_id,
  }) => {
    if (skip == 0) {
      setSkpInternalOutletsLoading(true);
    }
    setSkpInternalOutletParams({
      ...skpInternalOutletParams,
      loading: skip > 0,
      skip: skip,
    });
    const payload = {
      params: {
        filter: {
          'customer.id': customer_id,
          check_claim: true,
        },
        skip: skip,
        limit: skpInternalParams.limit,
        sort: '-_id',
      },
    };

    if (val) {
      payload.params.filter['$or'] = [
        { 'outlet.code': { $regex: val, $options: 'i' } },
        { 'outlet.name': { $regex: val, $options: 'i' } },
      ];
    }
    const response = await getSkpInternalOutlets.request(
      skp_internal_id,
      payload,
    );

    if (response.status == 200 && response?.data?.data?.length) {
      const old_result = JSON.parse(JSON.stringify(skpInternalOutlets));
      const result = response.data.data.map((item) => {
        if (item._id) {
          const isFullClaim = item.fully_claimed ? true : false;
          item.id = item._id;
          item.name = `${item.outlet.name} ${
            isFullClaim ? '(Fully Claimed)' : ''
          }`.trim();
          item.code = item.outlet.code;
          item.business_type = item.outlet.business_type;
          item.sales_team = item.outlet.sales_team;
          if (detailData?.outlet?.id != item.outlet.id) {
            item.disabled = isFullClaim;
          }
          item.is_pkp = item.tax_is_pkp;
          item.wht = item.tax_wht;
        }
        return item;
      });
      let finalResult = result;
      if (skip > 0) {
        finalResult = old_result.concat(result);
      }
      setSkpInternalOutlets(finalResult);
      if (skip > 0) {
        setSkpInternalOutletParams({
          ...skpInternalOutletParams,
          loading: false,
          skip: skip,
        });
      }
    } else {
      if (skip <= 0) {
        setSkpInternalOutlets([]);
      }
      setSkpInternalOutletParams({
        ...skpInternalOutletParams,
        loading: false,
        skip: skip,
      });
    }
    setSkpInternalOutletsLoading(false);
  };

  const getCoaTax = async (id) => {
    const coa_tax = await getCoaTaxSettingsDetail.request(id);
    const coaData = coa_tax?.data?.data;
    if (coaData) {
      coaData.vat = coaData.vat.map((v) => ({
        ...v,
        id: v._id,
        label: formatCoaTaxLabel(v),
      }));

      setCoaTaxData(coaData);
      return coaData;
    }
  };

  const getTaxPercentage = (
    skpExternal,
    coa_tax,
    skp_internal,
    customer,
    outlet,
  ) => {
    // Handle old skp data
    if (coa_tax) {
      let wht;
      if (skp_internal?.skp_type?.toLowerCase() == 'subdistributor') {
        wht = outlet?.wht;
      } else {
        wht = customer?.wht;
      }
      const { taxCorporate, taxPersonal } = getSkpInternalTaxes(
        coa_tax,
        skp_internal,
      );
      if (wht === 'npwp_badan') {
        const coaWht = (coa_tax?.tax_corporate || [])
          .map((t) => ({
            ...t,
            id: t._id,
            label: formatCoaTaxLabel(t),
          }))
          .filter((t) => !t.is_deleted);
        setTaxesData(coaWht);
        if (isEdit && !coaWht.find((t) => t.id == skpExternal.tax.id)) {
          skpExternal.tax = null;
        }
        return taxCorporate;
      } else if (wht === 'npwp_pribadi') {
        const coaWht = (coa_tax?.tax_personal || [])
          .map((t) => ({
            ...t,
            id: t._id,
            label: formatCoaTaxLabel(t),
          }))
          .filter((t) => !t.is_deleted);
        setTaxesData(coaWht);
        if (isEdit && !coaWht.find((t) => t.id == skpExternal.tax.id)) {
          skpExternal.tax = null;
        }
        return taxPersonal;
      } else {
        if (isEdit) {
          skpExternal.tax = null;
        }
      }
    }
    return null;
  };

  const getSkpInternalsBrandBudgets = async (skpInternal, customer, outlet) => {
    setSkpInternalsTableLoading(true);
    try {
      const filter = {
        'skp_internal.id': skpInternal.id,
        'customer.id': customer?.id,
      };
      if (outlet) {
        filter['outlet.id'] = outlet?.id;
      }
      const result = await getAllSkpInternalsBrandBudget.request({
        params: { filter },
      });

      const coa_tax = await getCoaTax(skpInternal?.coa_tax?.id);
      if (result?.data?.data?.length) {
        const taxData = getTaxPercentage(
          detailData,
          coa_tax,
          skpInternal,
          customer,
          outlet,
        );
        formRef.current.setFieldValue('tax', taxData);

        const defaultVat = coa_tax.vat.find((v) => v.is_default);
        if (defaultVat) {
          formRef.current.setFieldValue('vat', defaultVat);
        }

        // B2B-5936: MT & subdist start with Actual Claim/DPP Actual = 0; user must edit before requesting confirmation.
        const isGtSkp = isSkpType(skpInternal, 'gt');
        const budgetsWithActualClaim = result.data.data.map((b) => ({
          ...b,
          initial_budget: b.initial_budget || b.budget_sample,
          initial_dpp: b.initial_dpp || b.dpp,
          ...(!isGtSkp ? { budget_sample: 0, dpp: 0 } : {}),
        }));
        setSkpInternalsBrandBugets(budgetsWithActualClaim);
        calculateSkpInternalValues(
          skpInternal,
          budgetsWithActualClaim,
          customer,
          outlet,
          coa_tax,
          taxData,
          defaultVat,
        );
      }
    } catch (error) {
      console.log('SKP Internal Brand And Nominal Budget Not Found', error);
    }
    setSkpInternalsTableLoading(false);
  };

  const calculateSkpInternalValues = async (
    skpInternal,
    budgets,
    customer,
    outlet,
    coa_tax,
    tax,
    vat,
  ) => {
    try {
      const values = {};
      if (coa_tax) {
        values['nilai_aktivitas'] = 0;
        budgets.map((el) => (values['nilai_aktivitas'] += Number(el.dpp)));

        values['total_biaya'] = values['nilai_aktivitas'];

        let ppn_percentage = Number(vat.tax_value);

        let isPkp;
        if (skpInternal?.skp_type?.toLowerCase() == 'subdistributor') {
          isPkp = outlet?.is_pkp;
        } else {
          isPkp = customer?.is_pkp;
        }
        if (!isPkp) {
          ppn_percentage = 0;
        }

        const pph_percentage = Number(tax.tax_value);
        values['pph'] = values['total_biaya'] * (pph_percentage / 100);
        values['ppn'] = values['total_biaya'] * (ppn_percentage / 100);
        values['total_nett'] =
          values['total_biaya'] - values['pph'] + values['ppn'];

        formRef.current.setFieldValue(
          'total_biaya',
          Math.round(values.total_biaya),
        );
        formRef.current.setFieldValue(
          'nilai_aktivitas',
          Math.round(values.nilai_aktivitas),
        );
        formRef.current.setFieldValue('pph', Math.round(values.pph));
        formRef.current.setFieldValue('ppn', Math.round(values.ppn));
        formRef.current.setFieldValue(
          'total_nett',
          Math.round(values.total_nett),
        );
      }
    } catch (error) {}
  };

  const recalculateDpp = (skpInternal, budget, customer, outlet, tax) => {
    const source = budget.budget_sample;
    let dpp = source;

    let isPkp = customer.is_pkp;
    if (skpInternal?.skp_type?.toLowerCase() == 'subdistributor') {
      isPkp = outlet.is_pkp;
    }

    if (!isPkp) {
      const percentage = tax.tax_value;

      customer.pph_percentage = percentage;
      dpp = Math.round(source / (1 - percentage / 100));
    }
    return dpp || 0;
  };

  const getSkpExternalsBrandBudgets = async (
    skp_external_id,
    customer_id,
    outlet_id = null,
  ) => {
    setSkpInternalsTableLoading(true);
    try {
      const filter = {
        'skp_external.id': skp_external_id,
        'customer.id': customer_id,
      };
      if (outlet_id) {
        filter['outlet.id'] = outlet_id;
      }
      const result = await getAllSkpExternalsBrandBudget.request({
        params: { filter },
      });
      if (result?.data?.data?.length) {
        setSkpInternalsBrandBugets(
          result.data.data.map((b) => ({
            ...b,
            initial_budget: b.initial_budget || b.budget_sample,
            initial_dpp: b.initial_dpp || b.dpp,
          })),
        );
      } else {
        setSkpInternalsBrandBugets([]);
      }
    } catch (error) {
      console.log('SKP External Brand And Nominal Budget Not Found', error);
    }
    setSkpInternalsTableLoading(false);
  };

  const generatePayload = (payload) => {
    const skpInternal = {
      id: payload.skp_internal?.id,
      reference_code: payload.skp_internal?.reference_code,
      name: payload.skp_internal?.name,
      coa_tax: {
        id: payload.skp_internal?.coa_tax?.id,
        cost_type: payload.skp_internal?.coa_tax?.cost_type,
        coa_number: payload.skp_internal?.coa_tax?.coa_number,
        skp_type_flow: payload.skp_internal?.coa_tax?.skp_type_flow,

        tax_corporate: payload.skp_internal?.coa_tax?.tax_corporate,
        tax_personal: payload.skp_internal?.coa_tax?.tax_personal,
        non_tax_corporate: payload.skp_internal?.coa_tax?.non_tax_corporate,
        non_tax_personal: payload.skp_internal?.coa_tax?.non_tax_personal,
      },
      skp_type: payload.skp_internal?.skp_type?.toLowerCase(),
    };

    const documents = [];
    if (payload.kwitansi) {
      documents.push({
        type: 'kwitansi',
        url: payload.kwitansi,
      });
    }
    if (payload.faktur_pajak) {
      documents.push({
        type: 'faktur_pajak',
        url: payload.faktur_pajak,
      });
    }
    if (payload.foto_bukti_tayang) {
      documents.push({
        type: 'foto_bukti_tayang',
        url: payload.foto_bukti_tayang,
      });
    }
    if (payload.signed_skp) {
      documents.push({
        type: 'signed_skp',
        url: payload.signed_skp,
      });
    }
    if (payload.foto_buku_rekening) {
      documents.push({
        type: 'foto_buku_rekening',
        url: payload.foto_buku_rekening,
      });
    }
    if (payload.payment_proof) {
      documents.push({
        type: 'payment_proof',
        url: payload.payment_proof,
      });
    }
    if (payload.other?.length) {
      payload.other.forEach((item) => {
        documents.push({
          type: 'other',
          url: item.url,
        });
      });
    }
    const finalPayload = {
      skp_internal: skpInternal,
      from_date: payload.from_date,
      to_date: payload.to_date,
      is_active: true,
      customer: {
        id: payload.customer?.id,
        name: payload.customer?.name,
        market_type: payload.customer?.market_type,
        code: payload.customer?.customer_code || payload.customer?.code,
        wht: payload.customer?.wht || '',
        is_pkp: !!payload.customer?.is_pkp,
        key_account_manager: payload.customer?.key_account_manager,
        account_manager: payload.customer?.account_manager,
      },
      outlet: payload.outlet,
      sales_team: {
        id: payload.sales_team?.id,
        name: payload.sales_team?.name,
      },
      skp_external_account_no: payload.skp_external_account_no,
      kwitansi_no: payload.kwitansi_no,
      grossup_pph: payload.grossup_pph,
      total_biaya: Math.round(Number(payload.total_biaya)),
      pph: Math.round(Number(payload.pph)),
      ppn: Math.round(Number(payload.ppn)),
      total_nett: Math.round(Number(payload.total_nett)),
      nilai_aktivitas: Math.round(Number(payload.nilai_aktivitas)),
      documents,
      status: payload.status,
      reference_invoice_potong: payload.reference_invoice_potong,
      notes: payload.notes,
      tax: {
        ...payload.tax,
        tax_type: taxType,
      },
      claim_type: payload.claim_type || 'full',
      vat: payload.vat,
    };

    if (skpInternalsBrandBugets?.length) {
      const finalData = skpInternalsBrandBugets.map((el) => {
        delete el.skp_internal;
        delete el._id;
        delete el.created_at;
        delete el.updated_at;
        return el;
      });
      finalPayload.customer_brand_budgets = finalData;
    }

    return finalPayload;
  };

  const submitForm = async (payload) => {
    try {
      dispatch(showPopup({ name: 'isLoading', value: true }));
      const finalPayload = generatePayload(payload);

      if (finalPayload.status === 'need_approval') {
        const wasRejected = detailData?.state_histories?.some(
          (h) => h.to_status === 'rejected',
        );
        if (wasRejected) {
          finalPayload.is_resubmit = true;
        }
      }

      if (!skpInternalsBrandBugets?.length) {
        dispatch(showPopup({ name: 'isLoading', value: false }));
        Toast.show({
          text1:
            'Customer & brand data not exists! Please refresh and reselect the data!',
          type: 'error',
        });
        return;
      }

      if (!isEdit) {
        const createData = await MsOrder.createSkpExternal(finalPayload);
        if (createData?.data?.data) {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'Successfully added data',
            type: 'success',
          });
          props.navigation.navigate('SkpExternalDetailScreen', {
            id: createData?.data?.data?._id,
            name: createData?.data?.data?.reference_code,
            titleScreen: createData?.data?.data?.reference_code,
          });
        } else {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'A system error occurred',
            type: 'error',
          });
        }
      } else {
        const updateData = await MsOrder.updateSkpExternal(
          detailData._id,
          finalPayload,
        );
        if (updateData?.data?.data) {
          if (skpInternalsBrandBugets?.length && isTableChanged) {
            // Await so the brand-budget upsert completes before the detail
            // screen refetches — otherwise the new budget_sample races the
            // reload and appears unsaved.
            await saveSkpExternalBudgets(
              updateData.data.data._id,
              updateData.data.data.customer?.id,
              updateData.data.data.outlet?.id,
              skpInternalsBrandBugets,
              isTableChanged,
            );
          }
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'Successfully edited data',
            type: 'success',
          });
          props.navigation.navigate('SkpExternalDetailScreen', {
            id: updateData?.data?.data?._id,
            name: updateData?.data?.data?.reference_code,
            titleScreen: updateData?.data?.data?.reference_code,
          });
        } else {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'A system error occurred',
            type: 'error',
          });
        }
      }
      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (error) {
      console.log(error);
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: error?.response?.data?.message || 'A system error occurred',
        type: 'error',
      });
    }
  };

  const handleCalculation = async (payload) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));

    if (!skpInternalsBrandBugets?.length) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1:
          'Customer & brand data not exists! Please refresh and reselect the data!',
        type: 'error',
      });
      return;
    }

    // B2B-5936: MT & subdist cannot request confirmation with Nilai Aktivitas = 0
    if (
      payload.status == 'need_approval' &&
      !isGt &&
      !(Number(payload.nilai_aktivitas) > 0)
    ) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: 'Please edit Actual Claim first. Nilai Aktivitas cannot be 0.',
        type: 'error',
      });
      return;
    }

    if (['need_approval', 'joined'].includes(payload.status)) {
      try {
        const finalPayload = generatePayload(payload);
        const msg = [
          `You're about to submit this claim as a ${payload.claim_type} claim.`,
        ];
        if (skpInternalsBrandBugets?.length) {
          const calcRes = await MsOrder.checkSkpExternalCalculation({
            ...finalPayload,
            ...(isEdit
              ? {
                  _id: detailData?._id,
                }
              : {}),
          });
          const calcData = calcRes?.data?.data;
          if (!calcData?.success) {
            msg.push(
              `This claim exceeds the available budget by ${currency(
                Math.abs(calcData.budget_remaining),
                true,
              )}.`,
            );
          }
        }
        msg.push('Are you sure you want to proceed?');
        dispatch(showPopup({ name: 'isLoading', value: false }));
        dispatch(
          showAlert({
            show: true,
            title: 'Confirm Claim Submission',
            description: msg.join('\n'),
            onCallback: async () => {
              submitForm(payload);
            },
          }),
        );
      } catch (error) {
        console.log(error);
        dispatch(showPopup({ name: 'isLoading', value: false }));
        Toast.show({
          text1: error?.response?.data?.message || 'A system error occurred',
          type: 'error',
        });
      }
    } else {
      submitForm(payload);
    }
  };

  const saveSkpExternalBudgets = async (id, customer_id, outlet_id, data) => {
    try {
      const finalData = data.map((el) => {
        delete el.skp_internal;
        delete el._id;
        delete el.created_at;
        delete el.updated_at;
        el.skp_external = {
          id,
        };
        return el;
      });
      const payload = {
        skp_external_id: id,
        customer_id,
        outlet_id,
        data: finalData,
      };
      await MsOrder.createSkpExternalsBrandBudgetBulk(id, payload);
    } catch (err) {}
  };

  const changeActualClaim = (values) => {
    // Recompute DPP + dependent totals from the new Actual Claim values,
    // then close the modal.
    updateBudgetSample(
      formRef.current.values.skp_internal,
      formRef.current.values.tax,
      formRef.current.values.vat,
      formRef.current.values.customer,
      formRef.current.values.outlet,
      values,
    );
    dispatch(showPopup({ name: 'editActualClaim', value: false }));
  };

  const resetOutlet = (reset) => {
    setIsTableChanged(false);
    reset('grossup_pph', false);
    reset('total_biaya', 0);
    reset('nilai_aktivitas', 0);
    reset('pph', 0);
    reset('ppn', 0);
    reset('vat', '');
    reset('total_nett', 0);
    reset('outlet', '');
    reset('tax', '');
    reset('business_type', '');
    reset('sales_team', '');
    setSkpInternalsBrandBugets([]);
    setTaxesData([]);
  };

  const resetFormData = (reset) => {
    reset('customer', '');
    reset('customer_name', '');
    resetOutlet(reset);
  };

  const updateBudgetSample = async (
    skpInternalData,
    taxData,
    vatData,
    cusData = null,
    outletData = null,
    newBudgets = [],
  ) => {
    const customer = cusData || formRef.current.values.customer;
    const outlet = outletData || formRef.current.values.outlet;
    const skpInternal = skpInternalData || formRef.current.values.skp_internal;

    if (!taxData) {
      taxData = formRef.current.values.tax;
    }
    if (!vatData) {
      vatData = formRef.current.values.vat;
    }

    const newBudgetsDpp = newBudgets.map((budget) => {
      return {
        ...budget,
        dpp: recalculateDpp(skpInternal, budget, customer, outlet, taxData),
      };
    });
    setSkpInternalsBrandBugets(newBudgetsDpp);

    calculateSkpInternalValues(
      skpInternal,
      newBudgetsDpp,
      customer,
      outlet,
      coaTaxData,
      taxData,
      vatData,
    );

    setIsTableChanged(true);
  };

  const goBack = () => {
    props.navigation.replace('SkpExternalFormScreen', {});
    // Edit → back to the detail it was opened from; create → back to the list.
    if (isEdit) {
      props.navigation.navigate('SkpExternalDetailScreen', props.route.params);
    } else {
      props.navigation.navigate('SkpExternalScreen');
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setIsEdit(false);
      setIsTableChanged(false);
      setDetailData({});
      setIsReady(false);
      if (props?.route?.params?.id) {
        setIsEdit(true);
        getDetail(props.route.params.id);
      } else {
        setTimeout(() => {
          setIsReady(true);
        }, 500);
      }
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  const isGt = useMemo(() => {
    const skpInternal = skpInternalData || detailData?.skp_internal;
    if (skpInternal) {
      return isSkpType(skpInternal, 'gt');
    }
    return false;
  }, [detailData, skpInternalData]);

  return (
    <Layout.Container testID="skp-external-form-screen">
      {(isEdit && getSkpExternalDetail?.loading) || !isReady ? (
        <Layout.Wrapper>
          <Layout.Loader />
        </Layout.Wrapper>
      ) : (isEdit ? detailData && Object.keys(detailData).length : true) ? (
        <Formik
          innerRef={formRef}
          initialValues={{
            skp_internal:
              isEdit && detailData?.skp_internal ? detailData.skp_internal : '',
            skp_name:
              isEdit && detailData?.skp_internal?.name
                ? detailData.skp_internal.name
                : '',
            from_date:
              isEdit && detailData?.from_date ? detailData.from_date : '',
            to_date: isEdit && detailData?.to_date ? detailData.to_date : '',
            coa_activity_promo:
              isEdit && detailData?.skp_internal?.coa_tax?.cost_type
                ? detailData.skp_internal.coa_tax.cost_type
                : '',
            is_active: detailData?.is_active || true,
            customer: isEdit && detailData?.customer ? detailData.customer : '',
            outlet: isEdit && detailData?.outlet ? detailData.outlet : '',
            customer_name:
              isEdit && detailData?.customer?.name
                ? detailData.customer.name
                : '',
            business_type:
              isEdit && detailData?.customer?.market_type?.business_type?.code
                ? detailData.customer.market_type.business_type.code
                : '',
            sales_team:
              isEdit && detailData?.sales_team ? detailData.sales_team : '',
            skp_external_account_no:
              isEdit && detailData?.skp_external_account_no
                ? detailData.skp_external_account_no
                : '',
            kwitansi_no:
              isEdit && detailData?.kwitansi_no ? detailData.kwitansi_no : '',
            nilai_aktivitas:
              isEdit && detailData?.nilai_aktivitas
                ? detailData.nilai_aktivitas
                : 0,
            grossup_pph:
              isEdit && detailData?.grossup_pph
                ? detailData.grossup_pph
                : false,
            total_biaya:
              isEdit && detailData?.total_biaya ? detailData.total_biaya : 0,
            pph: isEdit && detailData?.pph ? detailData.pph : 0,
            ppn: isEdit && detailData?.ppn ? detailData.ppn : 0,
            total_nett:
              isEdit && detailData?.total_nett ? detailData.total_nett : 0,
            kwitansi: isEdit && detailData?.kwitansi ? detailData.kwitansi : '',
            faktur_pajak:
              isEdit && detailData?.faktur_pajak ? detailData.faktur_pajak : '',
            foto_bukti_tayang:
              isEdit && detailData?.foto_bukti_tayang
                ? detailData.foto_bukti_tayang
                : '',
            signed_skp:
              isEdit && detailData?.signed_skp ? detailData.signed_skp : '',
            foto_buku_rekening:
              isEdit && detailData?.foto_buku_rekening
                ? detailData.foto_buku_rekening
                : '',
            payment_proof:
              isEdit && detailData?.payment_proof
                ? detailData.payment_proof
                : '',
            other: isEdit && detailData?.other ? detailData.other : [],
            status: isEdit && detailData?.status ? detailData.status : '',
            reference_invoice_potong:
              isEdit && detailData?.reference_invoice_potong
                ? detailData.reference_invoice_potong
                : '',
            notes: isEdit && detailData?.notes ? detailData.notes : '',
            tax: isEdit && detailData?.tax ? detailData.tax : '',
            claim_type:
              isEdit && detailData?.claim_type ? detailData.claim_type : '',
            vat: isEdit && detailData?.vat ? detailData.vat : '',
          }}
          validationSchema={FormValidationSchema}
          onSubmit={handleCalculation}>
          {({
            setFieldValue,
            validateForm,
            handleSubmit,
            errors,
            touched,
            values,
          }) => (
            <>
              {isWeb ? (
                <Layout.Wrapper white={false}>
                  <Layout.Back
                    testID="skp-external-form-back"
                    title="SKP External"
                    onCallback={() => goBack()}
                  />
                  <Layout.Title
                    testID="skp-external-form-title"
                    title={`${isEdit ? `Edit` : `Add`} SKP External`}>
                    <View style={styles.row}>
                      {!isEdit || detailData?.status == 'draft' ? (
                        <Button
                          testID="skp-external-form-customer-joined-btn"
                          type="primary"
                          rounded
                          buttonStyle={{
                            width: 160,
                            backgroundColor: Colors.blue0,
                            marginRight: 10,
                          }}
                          title={
                            <SociollaText
                              type="bold"
                              style={{
                                fontSize: 14,
                                letterSpacing: 0.5,
                                color: Colors.white,
                              }}>
                              Customer Joined
                            </SociollaText>
                          }
                          onPress={(evt) => {
                            setFieldValue('status', 'joined');
                            handleSubmit(evt);
                          }}
                        />
                      ) : (
                        false
                      )}
                      {isEdit &&
                      ['draft', 'joined'].includes(detailData?.status) ? (
                        <View style={{ marginRight: 10 }}>
                          <Button
                            testID="skp-external-form-request-confirm-btn"
                            type="primary"
                            rounded
                            buttonStyle={{
                              width: 185,
                              marginRight: 10,
                              backgroundColor: Colors.blue0,
                            }}
                            disabled={detailData?.status != 'joined'}
                            title={
                              <SociollaText
                                type="bold"
                                style={{
                                  fontSize: 14,
                                  letterSpacing: 0.5,
                                  color: Colors.white,
                                }}>
                                Request Confirmation
                              </SociollaText>
                            }
                            onPress={(evt) => {
                              if (!values.signed_skp || !values.kwitansi) {
                                Toast.show({
                                  text1:
                                    'Signed SKP and Kwitansi documents are required',
                                  type: 'error',
                                });
                                return;
                              }
                              setFieldValue('status', 'need_approval');
                              handleSubmit(evt);
                            }}
                          />
                        </View>
                      ) : (
                        false
                      )}
                      <Button
                        testID="skp-external-form-save-btn"
                        type="primary"
                        rounded
                        buttonStyle={{ width: 120 }}
                        title={
                          <SociollaText
                            type="bold"
                            style={{
                              fontSize: 14,
                              letterSpacing: 0.5,
                              color: Colors.white,
                            }}>
                            {isEdit ? 'SAVE' : 'Save Draft'}
                          </SociollaText>
                        }
                        onPress={(evt) => {
                          setFieldValue(
                            'status',
                            detailData?.status == 'joined' ? 'joined' : 'draft',
                          );
                          handleSubmit(evt);
                        }}
                      />
                    </View>
                  </Layout.Title>
                </Layout.Wrapper>
              ) : (
                false
              )}
              <Layout.Wrapper rounded>
                <ScrollView
                  style={isMobileWeb ? { height: dimension.height - 85 } : {}}
                  contentContainerStyle={{ width: '100%' }}>
                  <Layout.FormWrapper
                    style={{
                      paddingHorizontal: 15,
                      paddingTop: 20,
                      ...(isWeb
                        ? {
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            paddingHorizontal: 20,
                            marginBottom: 15,
                          }
                        : {
                            paddingBottom: !isMobileWeb
                              ? isAndroid
                                ? 200
                                : 150
                              : 180,
                          }),
                    }}>
                    <View style={styles.rowForm}>
                      <Layout.FormGroup>
                        <SelectBox
                          testID="skp-external-form-skp-reference"
                          label="SKP Reference"
                          options={skpInternals}
                          value={values.skp_internal}
                          placeholder="Select Skp Reference"
                          trackBy="id"
                          labelKey="reference_code"
                          formLabelKey="reference_code"
                          isRequired
                          editable={!isEdit}
                          hasError={
                            errors.skp_internal && touched.skp_internal
                              ? errors.skp_internal
                              : null
                          }
                          loading={skpInternalsLoading}
                          withCallback={true}
                          withLoadMore
                          limit={skpInternalParams.limit}
                          loadMoreEmpty={
                            skpInternalParams.skip > skpInternals?.length
                          }
                          loadingCallback={skpInternalParams.loading}
                          onLoadMore={(keyword) => {
                            getSkpInternals({
                              val: keyword,
                              skip:
                                skpInternalParams.skip +
                                skpInternalParams.limit,
                            });
                          }}
                          searchCallback={(keyword) => {
                            getSkpInternals({
                              val: keyword,
                            });
                          }}
                          onReady={() => {
                            getSkpInternals({});
                          }}
                          onChange={async (value) => {
                            resetFormData(setFieldValue);
                            setSkpInternalData(value);
                            if (value.from_date) {
                              setFieldValue('from_date', value.from_date);
                            }
                            if (value.to_date) {
                              setFieldValue('to_date', value.to_date);
                            }
                            if (values.customer) {
                              setIsTableChanged(true);
                              getSkpInternalsBrandBudgets(
                                value,
                                values.customer,
                                values.outlet,
                              );
                            }
                            setFieldValue('skp_internal', value);
                            if (value.name) {
                              setFieldValue('skp_name', value.name);
                            }
                            if (value.coa_tax?.cost_type) {
                              setFieldValue(
                                'coa_activity_promo',
                                value.coa_tax.cost_type,
                              );
                            }
                          }}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <Input
                          testID="skp-external-form-skp-name"
                          label="SKP Reference Name"
                          value={values.skp_name}
                          isRequired
                          placeholder="SKP Name"
                          hasError={
                            errors.skp_name && touched.skp_name
                              ? errors.skp_name
                              : null
                          }
                          disabled
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <SociollaText style={{ fontSize: 11 }}>
                          SKP Reference Periode
                        </SociollaText>
                        <View
                          style={{
                            width: '100%',
                            flexDirection: 'row',
                            gap: 10,
                          }}>
                          <DatePicker
                            testID="skp-external-form-period-from-date"
                            label="From Date"
                            name="from_date"
                            disabled
                            style={{
                              marginRight: isWeb ? 10 : 0,
                              width: isWeb ? 'calc(50% - 5px)' : '49%',
                            }}
                            value={values.from_date}
                            disablePastDates={true}
                            hasError={
                              errors.from_date && touched.from_date
                                ? errors.from_date
                                : null
                            }
                          />
                          <DatePicker
                            testID="skp-external-form-period-to-date"
                            label="To Date"
                            name="to_date"
                            disabled
                            style={{ width: isWeb ? 'calc(50% - 5px)' : '49%' }}
                            value={values.to_date}
                            disablePastDates={true}
                            disableBeforeDate={values.from_date}
                            hasError={
                              errors.to_date && touched.to_date
                                ? errors.to_date
                                : null
                            }
                          />
                        </View>
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <Input
                          testID="skp-external-form-coa"
                          label="COA Activity Promo"
                          value={values.coa_activity_promo}
                          isRequired
                          placeholder="COA Activity Promo"
                          hasError={
                            errors.coa_activity_promo &&
                            touched.coa_activity_promo
                              ? errors.coa_activity_promo
                              : null
                          }
                          disabled
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <SelectBox
                          testID="skp-external-form-customer-code"
                          label="Customer Code"
                          options={skpInternalCustomers}
                          value={values.customer}
                          placeholder="Select Customer Code"
                          labelKey="label"
                          formLabelKey="code"
                          trackBy="code"
                          isRequired
                          hasError={
                            errors.customer && touched.customer
                              ? errors.customer
                              : null
                          }
                          useCallbackOptions={[values.skp_internal]}
                          editable={!!values.skp_internal}
                          loading={skpInternalCustomersLoading}
                          withCallback={true}
                          withLoadMore
                          limit={skpInternalCustomerParams.limit}
                          loadMoreEmpty={!isLoadMoreCustomer}
                          forceLoadMore={isLoadMoreCustomer}
                          loadingCallback={skpInternalCustomerParams.loading}
                          onLoadMore={(keyword) => {
                            findSkpInternalCustomers({
                              val: keyword,
                              skip:
                                skpInternalCustomerParams.skip +
                                skpInternalCustomerParams.limit,
                              skp_internal_id: values.skp_internal.id,
                            });
                          }}
                          searchCallback={(keyword) => {
                            findSkpInternalCustomers({
                              val: keyword,
                              skp_internal_id: values.skp_internal.id,
                            });
                          }}
                          onReady={() => {
                            findSkpInternalCustomers({
                              skp_internal_id: values.skp_internal.id,
                            });
                          }}
                          onChange={async (value) => {
                            if (values.skp_internal) {
                              if (
                                values.skp_internal?.skp_type?.toLowerCase() !=
                                'subdistributor'
                              ) {
                                setIsTableChanged(true);
                                getSkpInternalsBrandBudgets(
                                  values.skp_internal,
                                  value.customer,
                                  values.outlet,
                                );
                                if (
                                  value.customer.market_type?.business_type
                                    ?.code
                                ) {
                                  setFieldValue(
                                    'business_type',
                                    value.customer.market_type.business_type
                                      .code,
                                  );
                                }
                                getSalesTeambyCustomer(value.customer.id);
                              } else {
                                resetOutlet(setFieldValue);
                              }
                            }

                            setFieldValue('customer', value.customer);
                            if (value.customer.name) {
                              setFieldValue(
                                'customer_name',
                                value.customer.name,
                              );
                            }

                            if (value.customer.is_pkp) {
                              setFieldValue('grossup_pph', false);
                            } else {
                              setFieldValue('grossup_pph', true);
                            }
                            setCustomerTaxType(value.customer);
                          }}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <Input
                          testID="skp-external-form-customer-name"
                          label="Customer name"
                          value={values.customer_name}
                          isRequired
                          placeholder="Customer Name"
                          hasError={
                            errors.customer_name && touched.customer_name
                              ? errors.customer_name
                              : null
                          }
                          disabled
                        />
                      </Layout.FormGroup>
                      <RenderIf
                        condition={
                          detailData?.skp_internal?.skp_type?.toLowerCase() ==
                            'subdistributor' ||
                          skpInternalData?.skp_type?.toLowerCase() ==
                            'subdistributor'
                        }>
                        <Layout.FormGroup>
                          <SelectBox
                            testID="skp-external-form-outlet-name"
                            label="Outlet Name"
                            options={skpInternalOutlets}
                            value={values.outlet}
                            placeholder="Select outlet"
                            labelKey="name"
                            formLabelKey="name"
                            trackBy="code"
                            isRequired
                            hasError={
                              errors.outlet && touched.outlet
                                ? errors.outlet
                                : null
                            }
                            useCallbackOptions={[values.customer]}
                            editable={!!values.customer}
                            loading={skpInternalOutletsLoading}
                            withCallback={true}
                            withLoadMore
                            limit={skpInternalOutletParams.limit}
                            loadMoreEmpty={
                              skpInternalOutletParams.skip >
                              skpInternalOutletParams?.length
                            }
                            loadingCallback={skpInternalOutletParams.loading}
                            onLoadMore={(keyword) => {
                              findSkpInternalOutlets({
                                val: keyword,
                                skip:
                                  skpInternalOutletParams.skip +
                                  skpInternalOutletParams.limit,
                                skp_internal_id: values.skp_internal.id,
                                customer_id: values.customer.id,
                              });
                            }}
                            searchCallback={(keyword) => {
                              findSkpInternalOutlets({
                                val: keyword,
                                skp_internal_id: values.skp_internal.id,
                                customer_id: values.customer.id,
                              });
                            }}
                            onReady={() => {
                              findSkpInternalOutlets({
                                skp_internal_id: values.skp_internal.id,
                                customer_id: values.customer.id,
                              });
                            }}
                            onChange={async (value) => {
                              if (
                                values.customer &&
                                values.skp_internal &&
                                values.skp_internal?.skp_type?.toLowerCase() ==
                                  'subdistributor'
                              ) {
                                setIsTableChanged(true);
                                getSkpInternalsBrandBudgets(
                                  values.skp_internal,
                                  values.customer,
                                  value.outlet,
                                );
                              }

                              setFieldValue('outlet', value.outlet);
                              setFieldValue(
                                'business_type',
                                value.outlet.business_type.code,
                              );
                              setFieldValue(
                                'sales_team',
                                value.outlet.sales_team,
                              );
                              if (value.outlet.is_pkp) {
                                setFieldValue('grossup_pph', false);
                              } else {
                                setFieldValue('grossup_pph', true);
                              }
                              setCustomerTaxType(value.outlet);
                            }}
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <Input
                            testID="skp-external-form-outlet-code"
                            label="Outlet Code"
                            value={values.outlet?.code}
                            placeholder="Outlet Code"
                            disabled
                          />
                        </Layout.FormGroup>
                      </RenderIf>
                      <Layout.FormGroup>
                        <SelectBox
                          testID="skp-external-form-tax"
                          label="Tax (%)"
                          options={taxesData}
                          value={values.tax}
                          placeholder="Select Tax (%)"
                          trackBy="id"
                          labelKey="label"
                          formLabelKey="label"
                          isRequired
                          editable={!!values.skp_internal && !!values.customer}
                          hasError={
                            errors.tax && touched.tax ? errors.tax : null
                          }
                          limit={taxesData?.limit}
                          onChange={async (value) => {
                            setFieldValue('tax', value);
                            if (values.skp_internal || values.customer) {
                              setIsTableChanged(true);
                              updateBudgetSample(
                                values.skp_internal,
                                value,
                                values.vat,
                                values.customer,
                                values.outlet,
                                skpInternalsBrandBugets,
                              );
                            }
                          }}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <SelectBox
                          testID="skp-external-form-vat"
                          label="VAT (%)"
                          options={coaTaxData?.vat}
                          value={values.vat}
                          placeholder="Select VAT (%)"
                          trackBy="id"
                          labelKey="label"
                          formLabelKey="label"
                          isRequired
                          editable={!!values.skp_internal && !!values.customer}
                          hasError={
                            errors.vat && touched.vat ? errors.vat : null
                          }
                          onChange={async (value) => {
                            setFieldValue('vat', value);
                            if (values.skp_internal || values.customer) {
                              setIsTableChanged(true);
                              updateBudgetSample(
                                values.skp_internal,
                                values.tax,
                                value,
                                values.customer,
                                values.outlet,
                                skpInternalsBrandBugets,
                              );
                            }
                          }}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <Input
                          testID="skp-external-form-business-type"
                          label="Business Type"
                          value={values.business_type?.toUpperCase() || ''}
                          isRequired
                          placeholder="Business Type"
                          hasError={
                            errors.business_type && touched.business_type
                              ? errors.business_type
                              : null
                          }
                          disabled
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <Input
                          testID="skp-external-form-sales-team"
                          label="Sales Team"
                          value={values.sales_team.name}
                          isRequired
                          placeholder="Sales Team"
                          hasError={
                            errors.sales_team && touched.sales_team
                              ? errors.sales_team
                              : null
                          }
                          disabled
                        />
                      </Layout.FormGroup>
                      <RenderIf condition={!isGt}>
                        <Layout.FormGroup>
                          <RadioButton
                            testID="skp-external-form-claim-type"
                            label="Claim Type"
                            orientation="horizontal"
                            isRequired
                            value={values.claim_type}
                            data={CLAIM_TYPE_OPTIONS}
                            onChange={(value) => {
                              setFieldValue('claim_type', value);
                            }}
                            hasError={
                              errors.claim_type && touched.claim_type
                                ? errors.claim_type
                                : null
                            }
                          />
                        </Layout.FormGroup>
                      </RenderIf>
                      <RenderIf condition={!isGt}>
                        <Layout.FormGroup>
                          <Input
                            testID="skp-external-form-account-no"
                            label="SKP External Account Number"
                            value={values.skp_external_account_no}
                            isRequired
                            placeholder="Input Number"
                            hasError={
                              errors.skp_external_account_no &&
                              touched.skp_external_account_no
                                ? errors.skp_external_account_no
                                : null
                            }
                            onChangeText={(val) =>
                              setFieldValue('skp_external_account_no', val)
                            }
                          />
                        </Layout.FormGroup>
                      </RenderIf>
                      <RenderIf condition={!isGt}>
                        <Layout.FormGroup>
                          <Input
                            testID="skp-external-form-kwitansi-no"
                            label="Invoice / Kwitansi Number"
                            value={values.kwitansi_no}
                            isRequired
                            placeholder="Input Number"
                            hasError={
                              errors.kwitansi_no && touched.kwitansi_no
                                ? errors.kwitansi_no
                                : null
                            }
                            onChangeText={(val) =>
                              setFieldValue('kwitansi_no', val)
                            }
                          />
                        </Layout.FormGroup>
                      </RenderIf>
                      <Layout.FormGroup>
                        <Input
                          testID="skp-external-form-ref-invoice"
                          label="Reference Invoice Potong"
                          value={values.reference_invoice_potong}
                          isRequired
                          placeholder="Reference Invoice Potong"
                          hasError={
                            errors.reference_invoice_potong &&
                            touched.reference_invoice_potong
                              ? errors.reference_invoice_potong
                              : null
                          }
                          onChangeText={(val) =>
                            setFieldValue('reference_invoice_potong', val)
                          }
                        />
                      </Layout.FormGroup>
                      <RenderIf condition={!isGt && !!values.customer}>
                        <Text
                          testID="skp-external-form-edit-actual-claim-btn"
                          style={styles.edit_budget}
                          onPress={() => {
                            dispatch(
                              showPopup({
                                name: 'editActualClaim',
                                value: true,
                              }),
                            );
                          }}>
                          {translate('Edit Actual Claim')}
                        </Text>
                      </RenderIf>
                      <Layout.FormGroup>
                        {values.skp_internal && values.customer ? (
                          <SkpInternalBrandBudgetTable
                            data={skpInternalsBrandBugets}
                            loading={skpInternalsTableLoading}
                            isGt={isGt}
                          />
                        ) : (
                          <Input
                            label="Brand And Nominal Budget"
                            value={''}
                            placeholder="Brand And Nominal Budget"
                            disabled
                          />
                        )}
                      </Layout.FormGroup>
                    </View>
                    <View style={styles.rowForm}>
                      <Layout.FormGroup>
                        <Input
                          testID="skp-external-form-nilai-aktivitas"
                          label="Nilai Aktivitas"
                          value={values.nilai_aktivitas}
                          isRequired
                          placeholder="Nilai Aktivitas"
                          hasError={
                            errors.nilai_aktivitas && touched.nilai_aktivitas
                              ? errors.nilai_aktivitas
                              : null
                          }
                          disabled
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <Input
                          testID="skp-external-form-grossup-pph"
                          label="Grossup PPH"
                          value={values.grossup_pph ? 'Yes' : 'No'}
                          isRequired
                          placeholder="Select Grossup PPH"
                          hasError={
                            errors.grossup_pph && touched.grossup_pph
                              ? errors.grossup_pph
                              : null
                          }
                          disabled
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <Input
                          testID="skp-external-form-total-biaya"
                          label="Total Biaya/DPP(coa exp)"
                          value={values.total_biaya}
                          isRequired
                          placeholder="Total Biaya/DPP"
                          hasError={
                            errors.total_biaya && touched.total_biaya
                              ? errors.total_biaya
                              : null
                          }
                          disabled
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <Input
                          testID="skp-external-form-pph"
                          label="PPH(coa wht)"
                          value={values.pph}
                          isRequired
                          placeholder="PPH(coa wht)"
                          hasError={
                            errors.pph && touched.pph ? errors.pph : null
                          }
                          disabled
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <Input
                          testID="skp-external-form-ppn"
                          label="PPN(coa vat)"
                          value={values.ppn}
                          isRequired
                          placeholder="PPH(coa vat)"
                          hasError={
                            errors.ppn && touched.ppn ? errors.ppn : null
                          }
                          disabled
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <Input
                          testID="skp-external-form-total-nett"
                          label="Total Nett(coa ar)"
                          value={values.total_nett}
                          isRequired
                          placeholder="Total Nett(coa ar)"
                          hasError={
                            errors.total_nett && touched.total_nett
                              ? errors.total_nett
                              : null
                          }
                          disabled
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <FileInput
                          testID="skp-external-form-signed-skp"
                          confirmRemove
                          isRequired
                          label="Signed Skp"
                          value={getFileNameFromUrl(
                            values.signed_skp,
                            'Signed Skp',
                          )}
                          onChange={(value) => {
                            setFieldValue('signed_skp', value);
                          }}
                          containerStyleExtra={styles.uploadForm}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <FileInput
                          testID="skp-external-form-kwitansi"
                          confirmRemove
                          isRequired
                          label="Kwitansi"
                          value={getFileNameFromUrl(
                            values.kwitansi,
                            'Kwitansi',
                          )}
                          onChange={(value) => {
                            setFieldValue('kwitansi', value);
                          }}
                          containerStyleExtra={styles.uploadForm}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <FileInput
                          testID="skp-external-form-faktur-pajak"
                          confirmRemove
                          label="Faktur Pajak"
                          value={getFileNameFromUrl(
                            values.faktur_pajak,
                            'Faktur Pajak',
                          )}
                          onChange={(value) => {
                            setFieldValue('faktur_pajak', value);
                          }}
                          containerStyleExtra={styles.uploadForm}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <FileInput
                          testID="skp-external-form-foto-bukti-tayang"
                          confirmRemove
                          label="Foto Bukti Tayang"
                          value={getFileNameFromUrl(
                            values.foto_bukti_tayang,
                            'Foto Bukti Tayang',
                          )}
                          onChange={(value) => {
                            setFieldValue('foto_bukti_tayang', value);
                          }}
                          containerStyleExtra={styles.uploadForm}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <FileInput
                          testID="skp-external-form-foto-buku-rekening"
                          confirmRemove
                          label="Foto Buku Rekening"
                          value={getFileNameFromUrl(
                            values.foto_buku_rekening,
                            'Foto Buku Rekening',
                          )}
                          onChange={(value) => {
                            setFieldValue('foto_buku_rekening', value);
                          }}
                          containerStyleExtra={styles.uploadForm}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <FileInput
                          testID="skp-external-form-payment-proof"
                          confirmRemove
                          label="Payment Proof"
                          value={getFileNameFromUrl(
                            values.payment_proof,
                            'Payment Proof',
                          )}
                          onChange={(value) => {
                            setFieldValue('payment_proof', value);
                          }}
                          containerStyleExtra={styles.uploadForm}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <AttachmentsInput
                          testID="skp-external-form-other-file"
                          label="Other File"
                          value={values.other}
                          onChange={(updatedAttachments) => {
                            setFieldValue('other', updatedAttachments);
                          }}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <Input
                          testID="skp-external-form-notes"
                          label="Notes"
                          value={values.notes}
                          multiline={true}
                          numberOfLines={4}
                          placeholder="Notes"
                          hasError={
                            errors.notes && touched.notes ? errors.notes : null
                          }
                          onChangeText={(value) =>
                            setFieldValue('notes', value)
                          }
                        />
                      </Layout.FormGroup>
                    </View>
                  </Layout.FormWrapper>
                  {!isWeb && <View style={{ height: 210 }}></View>}
                </ScrollView>
              </Layout.Wrapper>
              {!isWeb && (isEdit ? !getSkpExternalDetail?.loading : true) ? (
                <Layout.ButtonSticky
                  testID="skp-external-form-sticky-buttons"
                  show={props?.route?.name == 'SkpExternalFormScreen'}
                  route={props?.route}
                  buttons={[
                    {
                      title: 'Customer Joined',
                      disabled:
                        !isEdit || detailData?.status == 'draft' ? false : true,
                      onPress: (evt) => {
                        setFieldValue('status', 'joined');
                        validateForm().then(() => {
                          handleSubmit(evt);
                        });
                      },
                    },
                    {
                      title: 'Request Confirmation',
                      disabled:
                        isEdit && ['joined'].includes(detailData?.status)
                          ? false
                          : true,
                      onPress: (evt) => {
                        if (!values.signed_skp || !values.kwitansi) {
                          Toast.show({
                            text1:
                              'Signed SKP and Kwitansi documents are required',
                            type: 'error',
                          });
                          return;
                        }
                        setFieldValue('status', 'need_approval');
                        validateForm().then(() => {
                          handleSubmit(evt);
                        });
                      },
                    },
                    {
                      title: 'Save Draft',
                      type: 'primary',
                      onPress: (evt) => {
                        setFieldValue('status', 'draft');
                        validateForm().then(() => {
                          handleSubmit(evt);
                        });
                      },
                    },
                  ]}
                />
              ) : (
                false
              )}
            </>
          )}
        </Formik>
      ) : (
        false
      )}
      {editActualClaim ? (
        <SkpExternalBudgetTableModal
          data={skpInternalsBrandBugets}
          changeActualClaim={changeActualClaim}
          computeDpp={(row) =>
            recalculateDpp(
              formRef.current.values.skp_internal,
              row,
              formRef.current.values.customer,
              formRef.current.values.outlet,
              formRef.current.values.tax,
            )
          }
        />
      ) : (
        false
      )}
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  row: { flexDirection: 'row' },
  rowForm: { width: isWeb ? 'calc(50% - 20px)' : '100%' },
  uploadForm: {
    borderRadius: 4,
    borderWidth: 1,
    height: isWeb ? 35 : 45,
    paddingHorizontal: 16,
    borderColor: Colors.grey0,
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  edit_budget: {
    ...(isWeb ? { cursor: 'pointer' } : {}),
    textAlign: 'right',
    color: Colors.blue0,
    fontSize: 12,
    marginRight: 10,
  },
});

export default SkpExternalFormScreen;
